import { InboxInIcon, StarIcon } from '@heroicons/react/outline';
import * as React from 'react';
import * as NotificationsApi from '@mam/main/Api/Pigeon';
import { useApi, useApiResults } from '@mam/main/ApiHooks';
import { DetailModal } from '@mam/main/Application/Notification/DetailModal';
import { Feed } from './Feed';
import { Route, useSearchParams } from '@mam/main/Router';
import { ActivityIndicator, InfiniteScroll, Tab, Tabs } from '@mam/main/UI';

export const NotificationsFeedContainer: React.FunctionComponent = () => {
	const params = useSearchParams();
	const notificationId = params.get('notificationId');
	const status = params.get('status') ?? NotificationsApi.NotificationStatus.All;

	const [results, loadNext, reload] = useApiResults<
		NotificationsApi.NotificationsRequest,
		NotificationsApi.NotificationsResponse,
		NotificationsApi.NotificationsError
	>(
		React.useCallback(
			(lastResponse) =>
				lastResponse === null || lastResponse.pagination.currentPage < lastResponse.pagination.totalPages
					? new NotificationsApi.NotificationsRequest((lastResponse?.pagination.currentPage ?? 0) + 1, 20, status)
					: null,
			[status]
		),
		[status]
	);

	const [apiSetRead] = useApi<
		NotificationsApi.SetReadRequest,
		NotificationsApi.SetReadResponse,
		NotificationsApi.SetReadError
	>();

	const [apiChangePriority] = useApi<
		NotificationsApi.ChangePriorityRequest,
		NotificationsApi.ChangePriorityResponse,
		NotificationsApi.ChangePriorityError
	>();

	// Update the state directly, without waiting for API reload, to make it feel instantaneous
	const [notifications, dispatch] = React.useReducer(notificationsReducer, [], () => []);

	React.useEffect(() => {
		const allNotifications: NotificationsApi.Notification[] = [];
		results.value?.map((values) => allNotifications.push(...values.items));
		dispatch({ type: 'load', notifications: allNotifications });
	}, [results]);

	const onSetRead = React.useCallback(
		async (notificationId: string) => {
			dispatch({ type: 'read', notificationId });
			await apiSetRead(new NotificationsApi.SetReadRequest([notificationId]));
			await reload(results.value?.length ?? 1);
		},
		[results]
	);

	const onSetReadAll = React.useCallback(
		async (notificationIds: string[]) => {
			dispatch({ type: 'readAll', notificationIds: new Set(notificationIds) });
			await apiSetRead(new NotificationsApi.SetReadRequest(notificationIds));
			await reload(results.value?.length ?? 1);
		},
		[results]
	);

	const onSetPriority = React.useCallback(
		async (notificationId: string, priority: boolean) => {
			dispatch({
				notificationId,
				priority: priority ? NotificationsApi.NotificationPriority.High : NotificationsApi.NotificationPriority.Normal,
				type: 'setPriority',
			});
			await apiChangePriority(new NotificationsApi.ChangePriorityRequest(priority, [notificationId]));
			await reload(results.value?.length ?? 1);
		},
		[results]
	);

	const BackLink: React.FunctionComponent<{ children: React.ReactElement<'a'>; status?: string }> = React.useCallback(
		(props) => <NotificationsFeedContainerRoute.Link {...props} />,
		[]
	);
	const redirectToFeed = NotificationsFeedContainerRoute.useRedirect();

	return (
		<>
			{notificationId !== null ? (
				<DetailModal
					notificationId={notificationId}
					onClose={() => {
						redirectToFeed({ status });
					}}
				/>
			) : null}
			<Tabs>
				<BackLink status={NotificationsApi.NotificationStatus.All.valueOf()}>
					<a>
						<Tab isSelected={status === NotificationsApi.NotificationStatus.All}>
							<InboxInIcon
								aria-hidden='true'
								className={`p-1 flex-shrink-0 inline h-7 w-7 cursor-pointer stroke-2 rounded-full`}
							/>{' '}
							All
						</Tab>
					</a>
				</BackLink>
				<BackLink status={NotificationsApi.NotificationStatus.Priority.valueOf()}>
					<a>
						<Tab isSelected={status === NotificationsApi.NotificationStatus.Priority}>
							<StarIcon
								aria-hidden='true'
								className={`p-1 flex-shrink-0 inline h-7 w-7 cursor-pointer stroke-2 rounded-full`}
							/>{' '}
							Priority
						</Tab>
					</a>
				</BackLink>
			</Tabs>
			{results.value === null ? (
				<ActivityIndicator />
			) : (
				<InfiniteScroll onLoad={loadNext}>
					<Feed
						notifications={notifications}
						status={status}
						onSetPriority={onSetPriority}
						onSetRead={onSetRead}
						onSetReadAll={onSetReadAll}
					/>
					{results.isLoading ? <ActivityIndicator /> : null}
				</InfiniteScroll>
			)}
		</>
	);
};

NotificationsFeedContainer.displayName = 'Application.Screens.NotificationsFeedContainer';

interface NotificationAction {
	readonly notificationId?: string;
	readonly notificationIds?: Set<string>;
	readonly notifications?: NotificationsApi.Notification[];
	readonly priority?: NotificationsApi.NotificationPriority;
	readonly type: 'load' | 'read' | 'readAll' | 'setPriority';
}

const notificationsReducer = (
	items: NotificationsApi.Notification[],
	action: NotificationAction
): NotificationsApi.Notification[] => {
	switch (action.type) {
		case 'load':
			return action.notifications ?? [];

		case 'read':
			return items.map((item) => {
				if (item.id !== action.notificationId) {
					return item;
				}

				return {
					...item,
					authenticatedRecipient: {
						...item.authenticatedRecipient,
						isRead: true,
						readTime: new Date(),
					},
				};
			});

		case 'readAll':
			return items.map((item) => {
				const shouldItemChange = action.notificationIds?.has(item.id) ?? false;
				if (!shouldItemChange) {
					return item;
				}

				return {
					...item,
					authenticatedRecipient: {
						...item.authenticatedRecipient,
						isRead: true,
						readTime: new Date(),
					},
				};
			});

		case 'setPriority':
			return items.map((item) => {
				if (item.id !== action.notificationId) {
					return item;
				}

				return {
					...item,
					authenticatedRecipient: {
						...item.authenticatedRecipient,
						priority: action.priority ?? NotificationsApi.NotificationPriority.Normal,
					},
				};
			});

		default:
			return items;
	}
};

export const NotificationsFeedContainerRoute = new Route<
	{
		notificationId?: string;
		status?: string;
	},
	typeof NotificationsFeedContainer
>(NotificationsFeedContainer, '/notifications', ({ notificationId = null, status = null }) => {
	const params = new URLSearchParams();
	if (notificationId !== null) {
		params.append('notificationId', notificationId);
	}
	if (status !== null) {
		params.append('status', status);
	}
	const query = params.toString();
	return `/notifications${query !== '' ? `?${query}` : ''}`;
});
